import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSearch, faFilter, faSort, faDownload, faChartBar, faSync, faChartPie, faEye } from '@fortawesome/free-solid-svg-icons';
import { startOfDay, endOfDay, subDays, startOfWeek, startOfMonth, format } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import AuditDetailModal from './AuditDetailModal';
import UplineStatsDashboard from './UplineStatsDashboard';
import { calculateUplineStatistics } from '../utils/uplineStatisticsCalculator';
import config from '../config/config';
import './AuditPage.css';

const AuditPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0); // API uses 0-based indexing
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    iboNumber: '',
    actionType: '',
    actionBy: '',
    orderId: '',
    paymentId: '',
    searchText: '',
    userName: '',
    userEmail: '',
    eventName: '',
    teamName: '',
    ticketType: '',
    ticketStatus: '',
    startDate: '',
    endDate: '',
    uplinePlatinumIbo: ''
  });
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('actionTime');
  const [sortDir, setSortDir] = useState('desc');
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Statistics state
  const [statistics, setStatistics] = useState({
    todayCount: 0,
    weekCount: 0,
    monthCount: 0,
    totalCount: 0,
    byActionType: {}
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  const [chartView, setChartView] = useState('pie'); // 'pie' or 'bar'
  
  // Date range state
  const [dateRangePreset, setDateRangePreset] = useState('');
  const [showDateRangeInfo, setShowDateRangeInfo] = useState(false);
  
  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState({
    enabled: false,
    interval: 30000, // 30 seconds
    lastUpdate: null
  });
  
  // Modal state
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Upline statistics state
  const [uplineStatistics, setUplineStatistics] = useState(null);
  const [appliedUplineIbo, setAppliedUplineIbo] = useState('');

  // Fetch events for the filter dropdown
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        
        const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/all`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.status === 'success') {
            setEvents(result.data || []);
          }
        }
      } catch (err) {
        console.error('Error fetching events:', err);
      }
    };
    
    fetchEvents();
  }, [getAccessToken]);

  // Fetch teams for the filter dropdown
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        
        const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.status === 'success') {
            setTeams(result.data || []);
          }
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
      }
    };
    
    fetchTeams();
  }, [getAccessToken]);

  useEffect(() => {
    fetchAuditLogs();
  }, [page, sortBy, sortDir, getAccessToken]);

  const fetchAuditLogs = async (customFilters = null, customPage = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Use custom filters if provided, otherwise use state filters
      const activeFilters = customFilters || filters;
      const activePage = customPage !== null ? customPage : page;
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: activePage,
        size: 20,
        sortBy: sortBy || 'actionTime',
        sortDir: sortDir
      });
      
      if (activeFilters.iboNumber) queryParams.append('iboNumber', activeFilters.iboNumber);
      if (selectedEventIds.length > 0) {
        selectedEventIds.forEach(id => queryParams.append('eventIds', id));
      }
      if (activeFilters.actionBy) queryParams.append('actionBy', activeFilters.actionBy);
      if (activeFilters.searchText) queryParams.append('searchText', activeFilters.searchText);
      if (activeFilters.eventName) queryParams.append('eventName', activeFilters.eventName);
      if (activeFilters.teamName) queryParams.append('teamName', activeFilters.teamName);
      if (activeFilters.uplinePlatinumIbo) queryParams.append('uplinePlatinumIbo', activeFilters.uplinePlatinumIbo);
      
      // Handle action types - use multiple selection if available, otherwise use single selection
      if (selectedActionTypes.length > 0) {
        selectedActionTypes.forEach(type => queryParams.append('actionTypes', type));
      } else if (filters.actionType) {
        queryParams.append('actionType', filters.actionType);
      }
      if (filters.startDate) {
        // Format as ISO string with time component
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);
        queryParams.append('startDate', startDate.toISOString());
      }
      if (filters.endDate) {
        // Format as ISO string with time component (end of day)
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        queryParams.append('endDate', endDate.toISOString());
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/audit?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs. Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setAuditLogs(result.data.content || []);
        setTotalPages(result.data.totalPages || 1);
      } else {
        throw new Error(result.message || 'Failed to load audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics for the dashboard
  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const token = await getAccessToken();
      if (!token) return;

      const now = new Date();
      const todayStart = startOfDay(now);
      const weekStart = startOfWeek(now);
      const monthStart = startOfMonth(now);

      // Fetch counts for different time periods
      const fetchCount = async (startDate, endDate) => {
        const queryParams = new URLSearchParams({
          page: 0,
          size: 1,
          sortBy: 'actionTime',
          sortDir: 'desc'
        });
        
        if (startDate) {
          queryParams.append('startDate', startDate.toISOString());
        }
        if (endDate) {
          queryParams.append('endDate', endDate.toISOString());
        }

        const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/audit?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          return result.data?.totalElements || 0;
        }
        return 0;
      };

      // Fetch all counts in parallel
      const [todayCount, weekCount, monthCount, totalCount] = await Promise.all([
        fetchCount(todayStart, now),
        fetchCount(weekStart, now),
        fetchCount(monthStart, now),
        fetchCount(null, null)
      ]);

      // Calculate action type breakdown from current audit logs
      const byActionType = auditLogs.reduce((acc, log) => {
        const type = log.actionType;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      setStatistics({
        todayCount,
        weekCount,
        monthCount,
        totalCount,
        byActionType
      });
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  // Fetch statistics when audit logs change
  useEffect(() => {
    if (auditLogs.length > 0) {
      fetchStatistics();
    }
  }, [auditLogs, getAccessToken]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh.enabled) return;
    
    const intervalId = setInterval(() => {
      fetchAuditLogs();
      fetchStatistics();
      setAutoRefresh(prev => ({ ...prev, lastUpdate: new Date() }));
    }, autoRefresh.interval);
    
    return () => clearInterval(intervalId);
  }, [autoRefresh.enabled, autoRefresh.interval]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      
      switch(e.key.toLowerCase()) {
        case 'f':
          e.preventDefault();
          setShowFilters(prev => !prev);
          break;
        case 'r':
          e.preventDefault();
          fetchAuditLogs();
          fetchStatistics();
          break;
        case 'escape':
          setExpandedRows(new Set());
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  
  // Calculate upline statistics when audit logs change and upline filter is active
  useEffect(() => {
    if (appliedUplineIbo) {
      if (auditLogs.length > 0) {
        const stats = calculateUplineStatistics(auditLogs);
        setUplineStatistics(stats);
      }
      // If appliedUplineIbo exists but auditLogs is empty, 
      // keep showing the dashboard with loading state
    } else {
      // Clear statistics only when upline filter is not applied
      setUplineStatistics(null);
    }
  }, [auditLogs, appliedUplineIbo]);

  // Get time since last update
  const getTimeSinceUpdate = () => {
    if (!autoRefresh.lastUpdate) return '';
    
    const seconds = Math.floor((new Date() - autoRefresh.lastUpdate) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  // Get date range display text
  const getDateRangeText = () => {
    if (!filters.startDate && !filters.endDate) {
      return 'All Time';
    }
    
    const start = filters.startDate ? format(new Date(filters.startDate), 'MMM d, yyyy') : 'Beginning';
    const end = filters.endDate ? format(new Date(filters.endDate), 'MMM d, yyyy') : 'Now';
    
    return `${start} - ${end}`;
  };

  // Get active filters for breadcrumb
  const getActiveFilters = useMemo(() => {
    const active = [];
    
    if (filters.iboNumber) active.push({ key: 'iboNumber', label: 'IBO', value: filters.iboNumber });
    if (selectedEventIds.length > 0) {
      const selectedEventNames = events
        .filter(e => selectedEventIds.includes(e.id))
        .map(e => e.name)
        .slice(0, 3)
        .join(', ');
      const displayValue = selectedEventIds.length <= 3 
        ? selectedEventNames 
        : `${selectedEventNames} +${selectedEventIds.length - 3} more`;
      active.push({ key: 'selectedEvents', label: 'Events', value: displayValue });
    }
    if (filters.eventName) active.push({ key: 'eventName', label: 'Event Name', value: filters.eventName });
    if (filters.actionType) active.push({ key: 'actionType', label: 'Action', value: filters.actionType.replace(/_/g, ' ') });
    if (selectedActionTypes.length > 0) active.push({ key: 'actionTypes', label: 'Actions', value: `${selectedActionTypes.length} selected` });
    if (filters.actionBy) active.push({ key: 'actionBy', label: 'Action By', value: filters.actionBy });
    if (filters.teamName) active.push({ key: 'teamName', label: 'Team', value: filters.teamName });
    if (filters.uplinePlatinumIbo) active.push({ key: 'uplinePlatinumIbo', label: 'Upline Platinum IBO', value: filters.uplinePlatinumIbo });
    if (filters.searchText) active.push({ key: 'searchText', label: 'Search', value: filters.searchText });
    if (filters.startDate || filters.endDate) {
      active.push({ key: 'dateRange', label: 'Date Range', value: getDateRangeText() });
    }
    
    return active;
  }, [filters, selectedActionTypes, events]);

  // Remove individual filter
  const removeFilter = (filterKey) => {
    const newFilters = { ...filters };
    
    if (filterKey === 'actionTypes') {
      setSelectedActionTypes([]);
    } else if (filterKey === 'selectedEvents') {
      setSelectedEventIds([]);
      setSelectedMonths(new Set());
    } else if (filterKey === 'dateRange') {
      newFilters.startDate = '';
      newFilters.endDate = '';
      setDateRangePreset('');
      setShowDateRangeInfo(false);
    } else {
      newFilters[filterKey] = '';
    }
    
    setFilters(newFilters);
    setPage(0);
    // Pass the new filters directly to fetchAuditLogs to avoid stale state
    fetchAuditLogs(newFilters, 0);
  };

  // Quick filter shortcuts
  const applyQuickFilter = (filterType) => {
    const now = new Date();
    let newFilters = { ...filters };
    
    switch(filterType) {
      case 'recent_purchases':
        newFilters.actionType = 'PURCHASE';
        newFilters.startDate = format(subDays(now, 7), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        setSelectedActionTypes([]);
        break;
      case 'recent_validations':
        setSelectedActionTypes(['VALIDATE', 'VALIDATE_QR', 'FIRST_YEAR_VALIDATE', 'VALIDATE_GENERIC_QR']);
        newFilters.actionType = '';
        newFilters.startDate = format(subDays(now, 7), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'all_transfers':
        setSelectedActionTypes(['TRANSFER_FROM', 'TRANSFER_TO', 'USER_TRANSFER_FROM', 'USER_TRANSFER_TO']);
        newFilters.actionType = '';
        break;
      default:
        break;
    }
    
    setFilters(newFilters);
    setPage(0);
    // Pass the new filters directly to fetchAuditLogs to avoid stale state
    fetchAuditLogs(newFilters, 0);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // If changing the single action type dropdown, clear the multiple action types checkboxes
    if (name === 'actionType' && value !== '') {
      setSelectedActionTypes([]);
    }
    
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleActionTypesChange = (actionType) => {
    // When using checkboxes, clear the single action type dropdown
    setFilters({
      ...filters,
      actionType: ''
    });
    
    setSelectedActionTypes(prev => {
      if (prev.includes(actionType)) {
        return prev.filter(type => type !== actionType);
      } else {
        return [...prev, actionType];
      }
    });
  };

  const handleEventSelection = (eventId, isChecked) => {
    setSelectedEventIds(prev => {
      if (isChecked) {
        return [...prev, eventId];
      } else {
        return prev.filter(id => id !== eventId);
      }
    });
  };

  const handleMonthSelection = (monthKey, eventIds, isChecked) => {
    setSelectedEventIds(prev => {
      if (isChecked) {
        // Add all events from this month that aren't already selected
        const newIds = eventIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      } else {
        // Remove all events from this month
        return prev.filter(id => !eventIds.includes(id));
      }
    });
    
    setSelectedMonths(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(monthKey);
      } else {
        newSet.delete(monthKey);
      }
      return newSet;
    });
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(0); // Reset to first page when filters change
    // Set the applied upline IBO for statistics dashboard
    setAppliedUplineIbo(filters.uplinePlatinumIbo);
    // Pass current filters to fetchAuditLogs with page 0
    fetchAuditLogs(filters, 0);
  };

  const resetFilters = () => {
    const emptyFilters = {
      iboNumber: '',
      actionType: '',
      actionBy: '',
      orderId: '',
      paymentId: '',
      searchText: '',
      userName: '',
      userEmail: '',
      eventName: '',
      teamName: '',
      ticketType: '',
      ticketStatus: '',
      startDate: '',
      endDate: '',
      uplinePlatinumIbo: ''
    };
    setFilters(emptyFilters);
    setSelectedActionTypes([]);
    setSelectedEventIds([]);
    setSelectedMonths(new Set());
    setAppliedUplineIbo(''); // Clear applied upline IBO
    setPage(0);
    // Pass empty filters directly to fetchAuditLogs to avoid stale state
    fetchAuditLogs(emptyFilters, 0);
  };

  const handleSortChange = (column) => {
    if (column === sortBy) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  // Handle clicking on stat cards to filter
  const handleStatClick = (statType, value) => {
    const now = new Date();
    let newFilters = { ...filters };
    
    switch(statType) {
      case 'today':
        newFilters.startDate = format(startOfDay(now), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'week':
        newFilters.startDate = format(startOfWeek(now), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'month':
        newFilters.startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'actionType':
        newFilters.actionType = value;
        setSelectedActionTypes([]);
        break;
      default:
        break;
    }
    
    setFilters(newFilters);
    setPage(0);
    // Pass the new filters directly to fetchAuditLogs to avoid stale state
    fetchAuditLogs(newFilters, 0);
  };

  // Handle date range preset selection
  const applyDatePreset = (preset) => {
    const now = new Date();
    let newFilters = { ...filters };
    
    switch(preset) {
      case 'today':
        newFilters.startDate = format(startOfDay(now), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'yesterday':
        const yesterday = subDays(now, 1);
        newFilters.startDate = format(startOfDay(yesterday), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(yesterday), 'yyyy-MM-dd');
        break;
      case 'last_7_days':
        newFilters.startDate = format(subDays(now, 7), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'last_30_days':
        newFilters.startDate = format(subDays(now, 30), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'this_month':
        newFilters.startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'last_month':
        const lastMonth = subDays(startOfMonth(now), 1);
        newFilters.startDate = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        newFilters.endDate = format(endOfDay(lastMonth), 'yyyy-MM-dd');
        break;
      case 'clear':
        newFilters.startDate = '';
        newFilters.endDate = '';
        setDateRangePreset('');
        break;
      default:
        break;
    }
    
    if (preset !== 'clear') {
      setDateRangePreset(preset);
      setShowDateRangeInfo(true);
    } else {
      setShowDateRangeInfo(false);
    }
    
    setFilters(newFilters);
    setPage(0);
    // Pass the new filters directly to fetchAuditLogs to avoid stale state
    fetchAuditLogs(newFilters, 0);
  };

  // Handle clickable cell navigation
  const handleCellClick = (e, filterType, value) => {
    e.stopPropagation(); // Prevent row expansion
    
    let newFilters = { ...filters };
    
    switch(filterType) {
      case 'iboNumber':
        newFilters.iboNumber = value;
        break;
      case 'eventId':
        newFilters.eventId = value;
        break;
      case 'eventName':
        newFilters.eventName = value;
        break;
      default:
        break;
    }
    
    setFilters(newFilters);
    setPage(0);
    // Pass the new filters directly to fetchAuditLogs to avoid stale state
    fetchAuditLogs(newFilters, 0);
  };

  // Open detail modal
  const handleViewDetail = (e, audit) => {
    e.stopPropagation(); // Prevent row expansion
    setSelectedAudit(audit);
    setShowDetailModal(true);
  };

  // Handle navigation from modal
  const handleModalNavigate = (filterType, value) => {
    setShowDetailModal(false);
    handleCellClick({ stopPropagation: () => {} }, filterType, value);
  };

  // Toggle row expansion
  const toggleRowExpansion = (auditId) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(auditId)) {
      newExpandedRows.delete(auditId);
    } else {
      newExpandedRows.add(auditId);
    }
    
    setExpandedRows(newExpandedRows);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Build export query parameters (include all current filters)
      const queryParams = new URLSearchParams();
      
      // Include filter parameters
      if (filters.iboNumber) queryParams.append('iboNumber', filters.iboNumber);
      
      // For eventIds, send multiple event IDs
      if (selectedEventIds.length > 0) {
        selectedEventIds.forEach(id => queryParams.append('eventIds', id));
      }
      
      if (filters.actionBy) queryParams.append('actionBy', filters.actionBy);
      if (filters.searchText) queryParams.append('searchText', filters.searchText);
      if (filters.eventName) queryParams.append('eventName', filters.eventName);
      if (filters.teamName) queryParams.append('teamName', filters.teamName);
      if (filters.uplinePlatinumIbo) queryParams.append('uplinePlatinumIbo', filters.uplinePlatinumIbo);
      
      // Handle action types - use multiple selection if available, otherwise use single selection
      if (selectedActionTypes.length > 0) {
        selectedActionTypes.forEach(type => queryParams.append('actionTypes', type));
      } else if (filters.actionType) {
        queryParams.append('actionType', filters.actionType);
      }
      
      // Include sorting parameters
      if (sortBy) queryParams.append('sortBy', sortBy);
      queryParams.append('sortDir', sortDir); // This has a default value
      
      // Format dates properly for the backend
      if (filters.startDate) {
        // Format as ISO string with time component
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);
        queryParams.append('startDate', startDate.toISOString());
      }
      
      if (filters.endDate) {
        // Format as ISO string with time component (end of day)
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        queryParams.append('endDate', endDate.toISOString());
      }
      
      console.log('Export URL:', `${config.API_BASE_URL}/em/v1/admin/audit/export?${queryParams.toString()}`);

      // Call the export endpoint
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/audit/export?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv', // Specify the expected content type
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        console.error('Export failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to export audit logs. Status: ${response.status}`);
      }
      
      // Get the file as a blob
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link to download the file
      const a = document.createElement('a');
      
      // Get filename from Content-Disposition header or use default
      let filename = 'audit_logs_export.csv';
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      setError(`Failed to export: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const formatEventDate = (dateStr, timezone) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      if (timezone) {
        return date.toLocaleString('en-US', {
          timeZone: timezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
          timeZoneName: 'short'
        });
      }
      return date.toLocaleString();
    } catch (e) {
      return dateStr;
    }
  };

  // Format action time from backend to readable date
  const formatActionTime = (actionTime) => {
    if (!actionTime) {
      return 'N/A';
    }
    
    // Handle ISO string format (new format)
    if (typeof actionTime === 'string') {
      try {
        const date = new Date(actionTime);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
        return date.toLocaleString();
      } catch (e) {
        console.error('Error parsing action time string:', e);
        return 'N/A';
      }
    }
    
    // Handle array format (legacy format)
    if (Array.isArray(actionTime) && actionTime.length >= 6) {
      try {
        const [year, month, day, hour, minute, second] = actionTime;
    // Create a date object (month is 0-indexed in JavaScript)
        const date = new Date(year, month - 1, day, hour, minute, second || 0);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
    return date.toLocaleString();
      } catch (e) {
        console.error('Error parsing action time array:', e);
        return 'N/A';
      }
    }
    
    return 'N/A';
  };

  // Get icon for action type
  const getActionTypeIcon = (actionType) => {
    const icons = {
      'PURCHASE': '🛒',
      'VALIDATE': '✅',
      'VALIDATE_QR': '📱',
      'FIRST_YEAR_VALIDATE': '🆕',
      'VALIDATE_GENERIC_QR': '🎫',
      'TRANSFER_FROM': '📤',
      'TRANSFER_TO': '📥',
      'USER_TRANSFER_FROM': '👤📤',
      'USER_TRANSFER_TO': '👤📥',
      'BULK_CREATE': '📦',
      'BULK_UPDATE': '🔄',
      'CANCELLATION': '❌',
      'REFUND': '💰',
      'UPDATE': '✏️',
      'DELETE': '🗑️'
    };
    return icons[actionType] || '📋';
  };

  // Get color for action type
  const getActionTypeColor = (actionType) => {
    const colors = {
      'PURCHASE': '#4CAF50',
      'VALIDATE': '#2196F3',
      'VALIDATE_QR': '#2196F3',
      'FIRST_YEAR_VALIDATE': '#2196F3',
      'VALIDATE_GENERIC_QR': '#2196F3',
      'TRANSFER_FROM': '#FF9800',
      'TRANSFER_TO': '#FF9800',
      'USER_TRANSFER_FROM': '#FF9800',
      'USER_TRANSFER_TO': '#FF9800',
      'BULK_CREATE': '#9C27B0',
      'BULK_UPDATE': '#9C27B0',
      'CANCELLATION': '#F44336',
      'REFUND': '#4CAF50',
      'UPDATE': '#2196F3',
      'DELETE': '#D32F2F'
    };
    return colors[actionType] || '#757575';
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    return Object.entries(statistics.byActionType)
      .map(([name, value]) => ({
        name: name.replace(/_/g, ' '),
        value,
        fullName: name,
        color: getActionTypeColor(name)
      }))
      .sort((a, b) => b.value - a.value);
  }, [statistics.byActionType]);

  // Find transfer pair for an audit log
  const findTransferPair = (audit) => {
    if (!audit.actionType.includes('TRANSFER')) return null;
    
    const isFrom = audit.actionType.includes('FROM');
    const targetType = isFrom ? 
      audit.actionType.replace('FROM', 'TO') : 
      audit.actionType.replace('TO', 'FROM');
    
    // Helper to convert actionTime to Date object
    const toDate = (actionTime) => {
      if (typeof actionTime === 'string') {
        return new Date(actionTime);
      }
      if (Array.isArray(actionTime) && actionTime.length >= 6) {
        const [year, month, day, hour, minute, second] = actionTime;
        return new Date(year, month - 1, day, hour, minute, second || 0);
      }
      return null;
    };
    
    const auditDate = toDate(audit.actionTime);
    if (!auditDate) return null;
    
    // Search within ±5 seconds
    return auditLogs.find(log => {
      if (log.actionType !== targetType || log.id === audit.id) return false;
      
      const logDate = toDate(log.actionTime);
      if (!logDate) return false;
      
      return Math.abs(logDate - auditDate) < 5000;
    });
  };

  // Check if audit is a transfer
  const isTransfer = (actionType) => {
    return actionType && actionType.includes('TRANSFER');
  };

  return (
    <div className="audit-container">
      <div className="page-title-section">
      <h2>Audit Logs</h2>
        <div className="keyboard-shortcuts-hint">
          <span className="hint-text">Keyboard Shortcuts:</span>
          <kbd>F</kbd> Toggle Filters
          <kbd>R</kbd> Refresh
          <kbd>Esc</kbd> Collapse All
        </div>
      </div>
      
      <button onClick={handleBack} className="back-button">
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
      </button>
      
      <div className="audit-actions">
        <div className="audit-actions-left">
        <button 
          className="toggle-filters-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} /> {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        
        <button 
          className="export-button"
          onClick={handleExport}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faDownload} /> Export to CSV
        </button>
        </div>
        
        <div className="audit-actions-right">
          <div className="auto-refresh-controls">
            <label className="auto-refresh-label">
              <input 
                type="checkbox"
                checked={autoRefresh.enabled}
                onChange={(e) => setAutoRefresh(prev => ({ 
                  ...prev, 
                  enabled: e.target.checked,
                  lastUpdate: e.target.checked ? new Date() : null
                }))}
              />
              Auto-refresh
            </label>
            
            {autoRefresh.enabled && (
              <>
                <select 
                  className="refresh-interval-select"
                  value={autoRefresh.interval}
                  onChange={(e) => setAutoRefresh(prev => ({ ...prev, interval: parseInt(e.target.value) }))}
                >
                  <option value={30000}>30s</option>
                  <option value={60000}>1min</option>
                  <option value={300000}>5min</option>
                </select>
                
                {autoRefresh.lastUpdate && (
                  <span className="last-update-time">
                    <FontAwesomeIcon icon={faSync} spin={loading} /> {getTimeSinceUpdate()}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {/* Quick Filter Shortcuts */}
      <div className="quick-filters-bar">
        <div className="quick-filters-label">Quick Filters:</div>
        <button 
          className="quick-filter-btn"
          onClick={() => applyQuickFilter('recent_purchases')}
        >
          Recent Purchases
        </button>
        <button 
          className="quick-filter-btn"
          onClick={() => applyQuickFilter('recent_validations')}
        >
          Recent Validations
        </button>
        <button 
          className="quick-filter-btn"
          onClick={() => applyQuickFilter('all_transfers')}
        >
          All Transfers
        </button>
      </div>
      
      {/* Active Filters Context Bar */}
      {getActiveFilters.length > 0 && (
        <div className="context-bar">
          <div className="context-header">
            <strong>Active Filters:</strong>
            <button 
              className="clear-all-filters"
              onClick={resetFilters}
            >
              Clear All
            </button>
          </div>
          <div className="filter-chips">
            {getActiveFilters.map((filter) => (
              <div key={filter.key} className="filter-chip">
                <span className="chip-label">{filter.label}:</span>
                <span className="chip-value">{filter.value}</span>
                <button 
                  className="chip-remove"
                  onClick={() => removeFilter(filter.key)}
                  title="Remove filter"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Quick Stats Summary Bar */}
      <div className="stats-summary-bar">
        <div className="stats-header">
          <h3><FontAwesomeIcon icon={faChartBar} /> Quick Statistics</h3>
          {loadingStats && <FontAwesomeIcon icon={faSync} spin className="stats-loading" />}
        </div>
        
        <div className="stats-grid">
          <div 
            className="stat-card stat-clickable" 
            onClick={() => handleStatClick('today')}
            title="Click to filter by today"
          >
            <div className="stat-value">{statistics.todayCount}</div>
            <div className="stat-label">Today</div>
          </div>
          
          <div 
            className="stat-card stat-clickable" 
            onClick={() => handleStatClick('week')}
            title="Click to filter by this week"
          >
            <div className="stat-value">{statistics.weekCount}</div>
            <div className="stat-label">This Week</div>
          </div>
          
          <div 
            className="stat-card stat-clickable" 
            onClick={() => handleStatClick('month')}
            title="Click to filter by this month"
          >
            <div className="stat-value">{statistics.monthCount}</div>
            <div className="stat-label">This Month</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-value">{statistics.totalCount}</div>
            <div className="stat-label">Total</div>
          </div>
        </div>
        
        {/* Action Type Breakdown */}
        {Object.keys(statistics.byActionType).length > 0 && (
          <div className="action-type-stats">
            <h4>Action Type Breakdown (Current Page)</h4>
            <div className="action-type-grid">
              {Object.entries(statistics.byActionType)
                .sort((a, b) => b[1] - a[1])
                .map(([actionType, count]) => (
                  <div 
                    key={actionType}
                    className={`action-type-stat stat-clickable action-type-${actionType.toLowerCase()}`}
                    onClick={() => handleStatClick('actionType', actionType)}
                    title={`Click to filter by ${actionType}`}
                  >
                    <span className="action-icon">{getActionTypeIcon(actionType)}</span>
                    <span className="action-name">{actionType.replace(/_/g, ' ')}</span>
                    <span className="action-count">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Charts Section */}
      {chartData.length > 0 && (
        <div className="charts-container">
          <div className="charts-header">
            <h3><FontAwesomeIcon icon={faChartPie} /> Action Type Distribution</h3>
            <div className="chart-controls">
              <button 
                className={`chart-toggle ${chartView === 'pie' ? 'active' : ''}`}
                onClick={() => setChartView('pie')}
              >
                Pie Chart
              </button>
              <button 
                className={`chart-toggle ${chartView === 'bar' ? 'active' : ''}`}
                onClick={() => setChartView('bar')}
              >
                Bar Chart
              </button>
              <button 
                className="chart-toggle-visibility"
                onClick={() => setShowCharts(!showCharts)}
              >
                {showCharts ? 'Hide Charts' : 'Show Charts'}
              </button>
            </div>
          </div>
          
          {showCharts && (
            <div className="chart-card">
              {chartView === 'pie' ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#ff9800">
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>
      )}
      
      {showFilters && (
        <div className="filters-panel">
          <h3>Filter Audit Logs</h3>
          <form onSubmit={applyFilters} className="filters-form">
            {/* Global Search */}
            <div className="filter-row">
              <div className="filter-group full-width">
                <label htmlFor="searchText">Global Search</label>
                <input
                  type="text"
                  id="searchText"
                  name="searchText"
                  placeholder="Search across Action Type, Action By, Details..."
                  value={filters.searchText}
                  onChange={handleFilterChange}
                />
              </div>
              </div>
              
            {/* Basic Filters */}
            <div className="filter-row">
              <div className="filter-group full-width">
                <label>Events (Select Multiple)</label>
                <div className="event-filter-container">
                  {(() => {
                    // Reuse existing grouping logic
                    const now = new Date();
                    const groupedEvents = {};
                    
                    events.forEach(event => {
                      if (!event.startDate) return;
                      const eventDate = new Date(event.startDate);
                      const sortKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
                      const monthYear = eventDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      
                      if (!groupedEvents[sortKey]) {
                        groupedEvents[sortKey] = {
                          label: monthYear,
                          events: [],
                          isFuture: eventDate >= now
                        };
                      }
                      
                      groupedEvents[sortKey].events.push({ ...event, eventDate });
                    });
                    
                    Object.values(groupedEvents).forEach(group => {
                      group.events.sort((a, b) => a.eventDate - b.eventDate);
                    });
                    
                    const sortedMonths = Object.keys(groupedEvents).sort((a, b) => {
                      const aIsFuture = groupedEvents[a].isFuture;
                      const bIsFuture = groupedEvents[b].isFuture;
                      if (aIsFuture && bIsFuture) return a.localeCompare(b);
                      if (!aIsFuture && !bIsFuture) return b.localeCompare(a);
                      return aIsFuture ? -1 : 1;
                    });
                    
                    return sortedMonths.map(sortKey => {
                      const group = groupedEvents[sortKey];
                      const monthEventIds = group.events.map(e => e.id);
                      const allSelected = monthEventIds.every(id => selectedEventIds.includes(id));
                      const someSelected = monthEventIds.some(id => selectedEventIds.includes(id));
                      
                      return (
                        <div key={sortKey} className="event-month-group">
                          <div className="month-header">
                            <label className="month-checkbox-label">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => {
                                  if (el) el.indeterminate = someSelected && !allSelected;
                                }}
                                onChange={(e) => handleMonthSelection(sortKey, monthEventIds, e.target.checked)}
                              />
                              <strong>
                                {group.isFuture ? '📅' : '📌'} {group.label} ({group.events.length} events)
                              </strong>
                            </label>
                          </div>
                          <div className="event-list">
                            {group.events.map(event => (
                              <label key={event.id} className="event-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={selectedEventIds.includes(event.id)}
                                  onChange={(e) => handleEventSelection(event.id, e.target.checked)}
                                />
                                <span className="event-name">{event.name}</span>
                                <span className="event-meta">
                                  {event.eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {selectedEventIds.length > 0 && (
                  <div className="selected-events-summary">
                    {selectedEventIds.length} event{selectedEventIds.length !== 1 ? 's' : ''} selected
                    <button 
                      type="button" 
                      className="clear-selection-btn"
                      onClick={() => setSelectedEventIds([])}
                    >
                      Clear Selection
                    </button>
                  </div>
                )}
              </div>

              <div className="filter-group">
                <label htmlFor="actionBy">Action By</label>
                <input
                  type="text"
                  id="actionBy"
                  name="actionBy"
                  placeholder="Enter Action By"
                  value={filters.actionBy}
                  onChange={handleFilterChange}
                />
            </div>
            
              <div className="filter-group">
                <label htmlFor="teamName">Team Name</label>
                <select
                  id="teamName"
                  name="teamName"
                  value={filters.teamName}
                  onChange={handleFilterChange}
                >
                  <option value="">All Teams</option>
                  {teams.map(team => (
                    <option key={team.id || team.name} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
              </div>
              
            {/* Upline Platinum IBO Filter */}
            <div className="filter-row">
              <div className="filter-group">
                <label htmlFor="uplinePlatinumIbo">
                  Upline Platinum IBO
                  <span className="filter-info-icon" title="Shows audit logs for all downlines of this upline">ℹ️</span>
                </label>
                <input
                  type="text"
                  id="uplinePlatinumIbo"
                  name="uplinePlatinumIbo"
                  placeholder="Enter Upline Platinum IBO"
                  value={filters.uplinePlatinumIbo}
                  onChange={handleFilterChange}
                />
                <small className="filter-hint">
                  Filters audits for all IBOs in the downline hierarchy (unlimited depth)
                </small>
              </div>
            </div>
              
            {/* Date Range Picker with Presets */}
            <div className="filter-section date-range-section">
              <h4>Date Range</h4>
              <div className="date-range-presets">
                <button 
                  type="button"
                  className={`preset-button ${dateRangePreset === 'today' ? 'active' : ''}`}
                  onClick={() => applyDatePreset('today')}
                >
                  Today
                </button>
                <button 
                  type="button"
                  className={`preset-button ${dateRangePreset === 'yesterday' ? 'active' : ''}`}
                  onClick={() => applyDatePreset('yesterday')}
                >
                  Yesterday
                </button>
                <button 
                  type="button"
                  className={`preset-button ${dateRangePreset === 'last_7_days' ? 'active' : ''}`}
                  onClick={() => applyDatePreset('last_7_days')}
                >
                  Last 7 Days
                </button>
                <button 
                  type="button"
                  className={`preset-button ${dateRangePreset === 'last_30_days' ? 'active' : ''}`}
                  onClick={() => applyDatePreset('last_30_days')}
                >
                  Last 30 Days
                </button>
                <button 
                  type="button"
                  className={`preset-button ${dateRangePreset === 'this_month' ? 'active' : ''}`}
                  onClick={() => applyDatePreset('this_month')}
                >
                  This Month
                </button>
                <button 
                  type="button"
                  className={`preset-button ${dateRangePreset === 'last_month' ? 'active' : ''}`}
                  onClick={() => applyDatePreset('last_month')}
                >
                  Last Month
                </button>
                <button 
                  type="button"
                  className="preset-button clear-button"
                  onClick={() => applyDatePreset('clear')}
                >
                  Clear
                </button>
              </div>
              
              {showDateRangeInfo && (
                <div className="date-range-display">
                  <strong>Showing:</strong> {getDateRangeText()}
                </div>
              )}
              
              <div className="filter-row">
              <div className="filter-group">
                  <label htmlFor="startDate">Custom From Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                    onChange={(e) => {
                      handleFilterChange(e);
                      setDateRangePreset('');
                      setShowDateRangeInfo(true);
                    }}
                />
              </div>
              
              <div className="filter-group">
                  <label htmlFor="endDate">Custom To Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                    onChange={(e) => {
                      handleFilterChange(e);
                      setDateRangePreset('');
                      setShowDateRangeInfo(true);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Types */}
            <div className="filter-row">
              <div className="filter-group">
                <label htmlFor="actionType">Single Action Type</label>
                <select
                  id="actionType"
                  name="actionType"
                  value={filters.actionType}
                  onChange={handleFilterChange}
                >
                  <option value="">All Actions</option>
                  <option value="PURCHASE">Purchase</option>
                  <option value="VALIDATE">Validate</option>
                  <option value="VALIDATE_QR">Validate QR</option>
                  <option value="FIRST_YEAR_VALIDATE">First Year Validate</option>
                  <option value="VALIDATE_GENERIC_QR">Validate Generic QR</option>
                  <option value="TRANSFER_FROM">Transfer From</option>
                  <option value="TRANSFER_TO">Transfer To</option>
                  <option value="USER_TRANSFER_FROM">User Transfer From</option>
                  <option value="USER_TRANSFER_TO">User Transfer To</option>
                  <option value="BULK_CREATE">Bulk Create</option>
                  <option value="BULK_UPDATE">Bulk Update</option>
                  <option value="CANCELLATION">Cancellation</option>
                  <option value="REFUND">Refund</option>
                  <option value="UPDATE">Update</option>
                  <option value="DELETE">Delete</option>
                </select>
              </div>
            </div>

            {/* Multiple Action Types Selection */}
            <div className="filter-row">
              <div className="filter-group full-width">
                <label>
                  Multiple Action Types 
                  <span className="filter-note"> (selecting any checkbox will clear the dropdown above)</span>
                </label>
                <div className="checkbox-group">
                  {['PURCHASE', 'VALIDATE', 'VALIDATE_QR', 'FIRST_YEAR_VALIDATE', 'VALIDATE_GENERIC_QR', 'TRANSFER_FROM', 'TRANSFER_TO', 'USER_TRANSFER_FROM', 'USER_TRANSFER_TO', 'BULK_CREATE', 'BULK_UPDATE', 'CANCELLATION', 'REFUND', 'UPDATE', 'DELETE'].map(actionType => (
                    <label key={actionType} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedActionTypes.includes(actionType)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleActionTypesChange(actionType);
                        }}
                      />
                      {actionType.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="filter-actions">
              <button type="button" onClick={resetFilters} className="reset-button">
                Reset Filters
              </button>
              <button type="submit" className="apply-button">
                <FontAwesomeIcon icon={faSearch} /> Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Upline Statistics Dashboard - Show independently of loading state */}
      {appliedUplineIbo && (
        <UplineStatsDashboard 
          statistics={uplineStatistics}
          uplineIbo={appliedUplineIbo}
          loading={loading || !uplineStatistics}
        />
      )}
      
      {loading ? (
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <div>Loading enriched audit logs...</div>
          <div className="loading-subtitle">Fetching user, event, and ticket information</div>
        </div>
      ) : (
        <>
          <div className="audit-table-container">
            <table className="audit-table">
              <thead>
                <tr>
                  <th onClick={() => handleSortChange('actionTime')} className="sortable">
                    Time
                    {sortBy === 'actionTime' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSortChange('actionType')} className="sortable">
                    Action
                    {sortBy === 'actionType' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSortChange('iboNumber')} className="sortable">
                    IBO Number
                    {sortBy === 'iboNumber' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSortChange('userName')} className="sortable">
                    IBO Name
                    {sortBy === 'userName' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSortChange('teamName')} className="sortable">
                    Team Name
                    {sortBy === 'teamName' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSortChange('eventName')} className="sortable">
                    Event
                    {sortBy === 'eventName' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSortChange('actionBy')} className="sortable">
                    Action By
                    {sortBy === 'actionBy' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length > 0 ? (
                  auditLogs.map((log) => {
                    const isExpanded = expandedRows.has(log.id);
                    const transferPair = findTransferPair(log);
                    const hasTransferPair = isTransfer(log.actionType) && transferPair;
                    return (
                      <React.Fragment key={log.id}>
                        <tr 
                          className={`audit-row-expandable ${isExpanded ? 'audit-row-expanded' : ''} ${hasTransferPair ? 'has-transfer-pair' : ''}`}
                          onClick={() => toggleRowExpansion(log.id)}
                          title="Click to expand/collapse"
                        >
                          <td>
                            {hasTransferPair && (
                              <span className="transfer-link-icon" title="Linked transfer">🔗</span>
                            )}
                            {formatActionTime(log.actionTime)}
                          </td>
                      <td className={`action-type ${log.actionType.toLowerCase()}`}>
                            {getActionTypeIcon(log.actionType)} {log.actionType.replace(/_/g, ' ')}
                          </td>
                          <td 
                            className="clickable-cell"
                            onClick={(e) => handleCellClick(e, 'iboNumber', log.iboNumber)}
                            title="Click to filter by this IBO"
                          >
                            {log.iboNumber}
                          </td>
                          <td>{log.userName || '-'}</td>
                          <td>{log.teamName || '-'}</td>
                          <td className="event-info">
                            {log.eventName && log.eventName !== '-' ? (
                              <div 
                                className="clickable-cell"
                                onClick={(e) => handleCellClick(e, 'eventName', log.eventName)}
                                title="Click to filter by this event"
                              >
                                {log.eventName}
                              </div>
                            ) : (
                              <div>-</div>
                            )}
                      </td>
                      <td>{log.actionBy}</td>
                      <td className="details-cell">{log.details}</td>
                    </tr>
                        {isExpanded && (
                          <tr className="expanded-content-row">
                            <td colSpan="8">
                              <div className="expanded-content">
                                <div className="expanded-header">
                                  <h4>Audit Details</h4>
                                  <button 
                                    className="view-full-detail-btn"
                                    onClick={(e) => handleViewDetail(e, log)}
                                  >
                                    <FontAwesomeIcon icon={faEye} /> View Full Detail
                                  </button>
                                </div>
                                
                                <div className="expanded-grid">
                                  <div className="expanded-section">
                                    <h5>Audit Information</h5>
                                    <div className="info-row">
                                      <span className="info-label">Audit ID:</span>
                                      <span className="info-value">{log.id}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Ticket ID:</span>
                                      <span className="info-value">{log.ticketId || 'N/A'}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Event ID:</span>
                                      <span className="info-value">{log.eventId || 'N/A'}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Event Start:</span>
                                      <span className="info-value">{formatEventDate(log.eventStartDate, log.eventTimezone)}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Event End:</span>
                                      <span className="info-value">{formatEventDate(log.eventEndDate, log.eventTimezone)}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="expanded-section">
                                    <h5>Transaction Details</h5>
                                    <div className="info-row">
                                      <span className="info-label">Order ID:</span>
                                      <span className="info-value">{log.orderId || 'N/A'}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Payment ID:</span>
                                      <span className="info-value">{log.paymentId || 'N/A'}</span>
                                    </div>
                                    {(log.paymentStatus || log.amount) && (
                                      <>
                                        <div className="info-row">
                                          <span className="info-label">Amount:</span>
                                          <span className="info-value">
                                            {log.amount ? `${log.currency || 'USD'} ${log.amount}` : 'N/A'}
                                          </span>
                                        </div>
                                        <div className="info-row">
                                          <span className="info-label">Payment Status:</span>
                                          <span className={`info-value payment-status ${log.paymentStatus?.toLowerCase()}`}>
                                            {log.paymentStatus || 'N/A'}
                                          </span>
                                        </div>
                                        <div className="info-row">
                                          <span className="info-label">Payment Method:</span>
                                          <span className="info-value">{log.paymentMethod || 'N/A'}</span>
                                        </div>
                                        <div className="info-row">
                                          <span className="info-label">Payment Provider:</span>
                                          <span className="info-value">{log.paymentProvider || 'N/A'}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  
                                  <div className="expanded-section">
                                    <h5>User Details</h5>
                                    <div className="info-row">
                                      <span className="info-label">Team:</span>
                                      <span className="info-value">{log.teamName || 'N/A'}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Phone:</span>
                                      <span className="info-value">{log.userPhone || 'N/A'}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Upline:</span>
                                      <span className="info-value">{log.uplinePlatinumIbo || 'N/A'}</span>
                                    </div>
                                    <div className="info-row">
                                      <span className="info-label">Upline Platinum Name:</span>
                                      <span className="info-value">{log.uplinePlatinumIboName || 'N/A'}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="expanded-section">
                                    <h5>Ticket Details</h5>
                                    <div className="info-row">
                                      <span className="info-label">Status:</span>
                                      <span className="info-value">{log.ticketStatus || 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Transfer Pair Information */}
                                {hasTransferPair && transferPair && (
                                  <div className="transfer-pair-section">
                                    <h5>🔗 Linked Transfer</h5>
                                    <div className="transfer-flow">
                                      <div className="transfer-from">
                                        <div className="transfer-label">
                                          {log.actionType.includes('FROM') ? 'From (This)' : 'From (Pair)'}
                                        </div>
                                        <div className="transfer-ibo">
                                          IBO: {log.actionType.includes('FROM') ? log.iboNumber : transferPair.iboNumber}
                                        </div>
                                      </div>
                                      <div className="transfer-arrow">→</div>
                                      <div className="transfer-to">
                                        <div className="transfer-label">
                                          {log.actionType.includes('TO') ? 'To (This)' : 'To (Pair)'}
                                        </div>
                                        <div className="transfer-ibo">
                                          IBO: {log.actionType.includes('TO') ? log.iboNumber : transferPair.iboNumber}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="transfer-details">
                                      <strong>Paired Action:</strong> {transferPair.actionType.replace(/_/g, ' ')} at {formatActionTime(transferPair.actionTime)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="no-records">
                      <div className="empty-state">
                        <div className="empty-state-icon">📋</div>
                        <h3>No Audit Records Found</h3>
                        <p>No audit logs match your current filter criteria.</p>
                        {getActiveFilters.length > 0 && (
                          <div className="empty-state-actions">
                            <button 
                              className="empty-state-btn"
                              onClick={resetFilters}
                            >
                              Clear All Filters
                            </button>
                            <button 
                              className="empty-state-btn secondary"
                              onClick={() => setShowFilters(true)}
                            >
                              Adjust Filters
                            </button>
                          </div>
                        )}
                        <div className="empty-state-tips">
                          <strong>Tips:</strong>
                          <ul>
                            <li>Try adjusting your date range</li>
                            <li>Remove some filters to broaden your search</li>
                            <li>Use the quick filters above for common searches</li>
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                disabled={page === 0}
                className="pagination-button"
              >
                Previous
              </button>
              
              <span className="page-info">
                Page {page + 1} of {totalPages}
              </span>
              
              <button 
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
                disabled={page === totalPages - 1}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Audit Detail Modal */}
      {showDetailModal && selectedAudit && (
        <AuditDetailModal
          audit={selectedAudit}
          onClose={() => setShowDetailModal(false)}
          onNavigate={handleModalNavigate}
        />
      )}
    </div>
  );
};

export default AuditPage; 